import { createContext, useContext, ReactNode } from 'react'
import { useAccount } from 'wagmi'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { sacrificeApi } from '../config/sacrifice-api'
import { useAuth, useProjects } from '.'
import type { AdminProjectResponse } from 'sacrifice-utils/api'

interface AdminProjectContextValue {
  data: AdminProjectResponse | undefined
  isLoading: boolean
  error: Error | null
  refreshData: () => void
  clearData: () => void
}

const AdminProjectContext = createContext<AdminProjectContextValue | undefined>(undefined)

export const useAdminProject = (): AdminProjectContextValue => {
  const context = useContext(AdminProjectContext)
  if (context === undefined) {
    throw new Error('useAdminProject must be used within an AdminProjectProvider')
  }
  return context
}

interface AdminProjectProviderProps {
  children: ReactNode
}

export const AdminProjectProvider = ({ children }: AdminProjectProviderProps) => {
  const { address } = useAccount()
  const { authData, isSignedIn } = useAuth()
  const { projectId } = useProjects()
  const queryClient = useQueryClient()

  // Use React Query for data fetching with automatic caching (5 minute stale time)
  const { data, isLoading, error, refetch } = useQuery<AdminProjectResponse, Error>({
    queryKey: ['adminProject', projectId, address],
    queryFn: async ({ signal }) => {
      if (!address || !projectId || !authData || !isSignedIn) {
        throw new Error('User must be signed in to fetch admin project')
      }

      const signature = authData.signature
      const timestamp = authData.inputs.timestamp.toString()

      return await sacrificeApi.admin.getProject({
        request: {
          signature,
          projectId,
          inputs: {
            timestamp: BigInt(timestamp),
          },
        },
        signal,
      })
    },
    enabled: Boolean(address && projectId && authData && isSignedIn),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
    retry: 1,
  })

  const refreshData = () => {
    refetch()
  }

  const clearData = () => {
    queryClient.removeQueries({ queryKey: ['adminProject'] })
  }

  const value: AdminProjectContextValue = {
    data,
    isLoading,
    error,
    refreshData,
    clearData,
  }

  return <AdminProjectContext.Provider value={value}>{children}</AdminProjectContext.Provider>
}
